import { defineStore } from 'pinia';
import mitt, { type Emitter } from 'mitt';

import type { UserSubscription, UserSubscriptionItem } from '@clovyr/pollen';
import type { Manifest } from '@clovyr/pollen/manifest';

import type { LoginProps } from '@/views/Settings/AuthForms/LoginForm.vue';
import type { SignupProps } from '@/views/Settings/AuthForms/SignupForm.vue';
import type { UnlockProps } from '@/views/Settings/AuthForms/Unlock.vue';
import type { UnlockSecretProps } from '@/views/Settings/AuthForms/UnlockSecret.vue';
import type { RecoveryKitDownloadProps } from '@/views/Settings/RecoveryKitDownload.vue';
import type { ShowAuthForm } from '@/views/Settings/types';

export interface ShowPaymentModalEvent {
  id: string;
  name: string;
  price?: number;
  sub: UserSubscription;
  subItem: UserSubscriptionItem;
}

export interface ShowLaunchPaymentModalEvent {
  app: Manifest;
}

interface ShowLogin {
  form?: ShowAuthForm.Login;
  /**
   * If user cancels log in, redirect to home page.
   */
  redirOnCancel?: boolean;
  opts?: LoginProps;
}

interface ShowSignup {
  form: ShowAuthForm.Signup;
  /**
   * If user cancels signup, redirect to home page.
   */
  redirOnCancel?: boolean;
  opts?: SignupProps;
}

// type ShowLockedAccount = {
//   form: ShowAuthForm.LockedAccount;
//   opts?: undefined;
// };

interface ShowNostrEmail {
  form: ShowAuthForm.NostrEmail;
  opts?: undefined;
}

interface ShowRecoveryKitDownload {
  form: ShowAuthForm.RecoveryKitDownload;
  opts: RecoveryKitDownloadProps;
}

interface ShowUnlockSecret {
  form: ShowAuthForm.SecretKeyForm;
  /**
   * If user cancels unlock, redirect to home page.
   */
  redirOnCancel?: boolean;
  opts?: UnlockSecretProps;
}

interface ShowLocked {
  form: ShowAuthForm.Locked;
  /**
   * If user cancels unlock, redirect to home page.
   */
  redirOnCancel?: boolean;
  opts?: UnlockProps;
}

interface ShowResendVerification {
  form: ShowAuthForm.ResendVerification;
  opts?: undefined;
}

interface ShowVerificationComplete {
  form: ShowAuthForm.VerificationComplete;
  opts?: undefined;
}

export type ShowLoginEvent =
  | ShowLogin
  | ShowSignup
  | ShowNostrEmail
  | ShowRecoveryKitDownload
  | ShowUnlockSecret
  | ShowLocked
  | ShowResendVerification
  | ShowVerificationComplete;
// | ShowLockedAccount

export type CaptchaStatus = { success: false } | { success: true; token: string };

export interface ConfirmModalEvent {
  title?: string;
  text?: string;
  okLabel?: string;
  cancelLabel?: string;
  resolve?: (result: boolean) => void;
}

// eslint-disable-next-line @typescript-eslint/consistent-type-definitions
type Events = {
  'drawer:close': void;
  'app:update': { appID: string };
  'captcha:complete': CaptchaStatus;
  'modal:login:show': ShowLoginEvent | undefined;
  'modal:login:close': void;
  'modal:login:force_close': void;
  'modal:unload:show': void;
  'modal:payment:show': ShowPaymentModalEvent;
  'modal:launch_payment:show': ShowLaunchPaymentModalEvent;
  'modal:launch_payment:close': void;
  'launch:continue': void; // when showing the payment modal, indicates that 'continue' was selected
  'auth:signup:complete': void;
  'auth:success': boolean;
  'overlay:launching:show': void;
  'overlay:launching:status': string;
  'overlay:launching:hide': void;
  'toast:show': {
    message: string;
    type: 'success' | 'info' | 'error';
    duration?: number;
    sticky?: boolean;
    data?: object;
  };
  'modal:confirm:show': ConfirmModalEvent;
};

/**
 * Simple store for using a shared global event bus for when we need to send events async across the
 * app without a simple parent/child component relationship.
 */
export const useEventBus = defineStore('event_bus', () => {
  const eventBus: Emitter<Events> = mitt();

  /**
   * Utility function to listen to an event only once.
   * Automatically unregisters the listener after the event is triggered.
   */
  function once<T extends keyof Events>(event: T, callback: (payload: Events[T]) => void) {
    const handler = (payload: Events[T]) => {
      callback(payload);
      eventBus.off(event, handler); // Unregister the listener after it is triggered
    };
    eventBus.on(event, handler);
  }

  return {
    eventBus,
    once,
  };
});
